import { styled } from 'goober';

import { ButtonIcon } from './ButtonIcons';

import type { WidgetZIndexLayers } from '../utils/zIndexLayers';
import type { ButtonIconName, WidgetState, WorknetWidgetConfig } from '@worknet/models';
import type { ComponentProps } from 'preact';

type Side = WorknetWidgetConfig['styles']['side'];
export const BTN_HEIGHT = 72;

// Wrapper handles absolute positioning relative to the widget root
const ButtonWrapper = styled('div')<{ side: Side; zIndex?: number }>`
  position: absolute;
  bottom: 0;
  z-index: ${(p) => p.zIndex ?? 'auto'};
  ${(p) => (p.side === 'left' ? 'left: 0;' : 'right: 0;')}
`;

type FabVisualProps = {
  buttonText?: string;
  hasIcon?: boolean;
  isActive?: boolean;
  isColorsReversed?: boolean;
  widgetState?: WidgetState;
  isDrawer?: boolean;
  disableHover?: boolean;
  closing?: boolean;
  opening?: boolean;
  compactIcon?: boolean;
  overlayZIndex?: number;
  textZIndex?: number;
};

const Alert = styled('span')<{ side: Side; buttonHasText: boolean; zIndex: number }>`
  position: absolute;
  top: ${(p) => (p.buttonHasText ? '-12px' : '-4px')};
  ${(p) =>
    p.buttonHasText
      ? p.side === 'left'
        ? 'left: 10px;'
        : 'right: 10px;'
      : p.side === 'left'
        ? 'left: -4px;'
        : 'right: -4px;'}
  width: 30px;
  height: 30px;
  padding: 0 5px;
  border-radius: 999px;
  border: 2px solid #fff;
  background: #cdd7ff;
  color: #587aff;
  font-size: 15px;
  line-height: 18px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: ${(p) => p.zIndex};
  pointer-events: none;

  /* Sync with button entrance animation */
  animation: alertBadgeEntrance 400ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards;

  @keyframes alertBadgeEntrance {
    0% {
      opacity: 0;
      transform: scale(0.5) translateY(20px);
    }
    60% {
      opacity: 1;
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const VisualButton = styled('button')<FabVisualProps>`
  --btn-bg: ${(p) => (p.isColorsReversed ? 'var(--clr-primary)' : 'var(--clr-secondary)')};
  --btn-fg: ${(p) => (p.isColorsReversed ? 'var(--clr-secondary)' : 'var(--clr-primary)')};
  --hover-overlay: ${(p) => (p.isColorsReversed ? 'var(--clr-secondary)' : 'var(--clr-primary)')};
  --icon-size: ${(p) => (p.compactIcon ? '28px' : '32px')};
  --btn-size: ${(p) => (p.buttonText ? 'auto' : `${BTN_HEIGHT}px`)};
  --border-radius: ${(p) => (p.buttonText ? '14px' : '50%')};

  background-color: var(--btn-bg);
  color: var(--btn-fg);
  transition:
    background-color 300ms ease,
    color 300ms ease,
    border-color 300ms ease,
    opacity 300ms ease,
    transform 300ms cubic-bezier(0.4, 0, 0.2, 1);

  font-size: 16px;
  font-weight: 500;
  line-height: 1.125em;
  overflow: hidden;
  position: relative;
  perspective: 600px;
  border: 1px solid var(--clr-primary);
  outline: none;
  display: flex;
  gap: 8px;
  cursor: pointer;
  pointer-events: auto;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  box-shadow:
    rgba(50, 50, 93, 0.25) 0 6px 12px -2px,
    rgba(0, 0, 0, 0.3) 0 3px 7px -3px;
  border-radius: var(--border-radius);
  ${(p) =>
    p.buttonText
      ? 'padding:18px 36px;'
      : 'width:var(--btn-size); height:var(--btn-size); padding:0;'}

  /* Initial entrance animation */
  animation: fabButtonEntrance 400ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards;

  @keyframes fabButtonEntrance {
    0% {
      opacity: 0;
      transform: scale(0.7) translateY(20px);
    }
    60% {
      opacity: 1;
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--hover-overlay);
    transform: translateY(100%);
    transition: transform 0.5s;
    will-change: transform;
    z-index: ${(p) => p.overlayZIndex ?? 0};
    pointer-events: none;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    opacity: 0;
    animation: showOverlay 400ms forwards;
  }

  @keyframes showOverlay {
    0%,
    99% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  &:hover::before {
    transform: translateY(0);
    color: var(--btn-bg);
  }

  &:hover {
    border-color: var(--btn-bg);
    outline: 1px solid var(--btn-bg);
    outline-offset: 0px;
  }

  .icon-wrap {
    ${(p) => (p.hasIcon ? 'display:flex; align-items:center; justify-content:center;' : '')}
    ${(p) => (!p.hasIcon ? 'display:none;' : '')}
    ${(p) => (p.buttonText && p.hasIcon ? 'flex:0 0 auto;' : '')}
  }
  .icon {
    width: var(--icon-size);
    height: var(--icon-size);
    display: block;
    flex: 0 0 var(--icon-size);
    transition:
      color 300ms ease,
      transform 600ms ease;
    will-change: transform;
    transform-origin: 50% 50%;
    backface-visibility: hidden;
  }
  .btn-text {
    position: relative;
    z-index: ${(p) => p.textZIndex ?? 1};
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.25px;
    white-space: nowrap;
    ${(p) => (p.buttonText ? 'flex:1 1 auto; text-align:left;' : '')}
    color: currentColor;
    transition: color 300ms ease;
  }

  &:hover {
    color: var(--btn-bg);
  }
  .btn-text[data-truncate='true'] {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &:hover .icon {
    color: var(--btn-bg);
    transform: rotateY(360deg);
  }
  &:hover .btn-text {
    color: var(--btn-bg);
  }
  ${(p) =>
    p.isColorsReversed &&
    `
      &:hover .icon { color: var(--btn-bg); }
      &:hover .btn-text { color: var(--btn-bg); }
    `}

  /* Suppress hover color swap when reversed + popup (not drawer) + chat open */
  ${(p) =>
    !p.isDrawer &&
    p.isColorsReversed &&
    p.widgetState === 'chat' &&
    `
      &::before { display:none; }
      &:hover::before { transform: translateY(100%); }
          /* Keep original foreground color */
          &:hover .icon { color: var(--btn-fg); transform: rotateY(360deg); }
          &:hover .btn-text { color: var(--btn-fg); }
        `}

  ${(p) =>
    p.disableHover &&
    `
      &::before { display:none; }
      &:hover::before { transform: translateY(100%); }
          &:hover .icon { color: var(--primary); transform:none; }
        `}
  @keyframes worknet-rotateY {
    to {
      transform: rotateY(360deg);
    }
  }
  @keyframes worknet-popup-pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1) rotateY(180deg);
    }
    100% {
      transform: scale(1) rotateY(360deg);
    }
  }

  /* Spinning animation for popup mode icon transitions - same as hover */
  .icon.spinning {
    animation: worknet-rotateY 400ms linear !important;
  }

  ${(p) =>
    p.closing
      ? `
      ${p.isDrawer ? 'pointer-events: none;' : ''}
      .icon { 
        animation: worknet-rotateY ${!p.isDrawer ? '320ms' : '480ms'} linear !important; 
      }
    `
      : ''}
  ${(p) =>
    p.opening &&
    `
      .icon { 
        animation: worknet-rotateY ${!p.isDrawer ? '380ms' : '480ms'} linear !important; 
      }
    `}
`;

type FabButtonProps = ComponentProps<'button'> &
  FabVisualProps & {
    side: Side;
    icon?: ButtonIconName;
    alertCount?: number;
    zIndexLayers?: WidgetZIndexLayers;
  };

export function FabButton({
  side,
  buttonText,
  icon,
  isColorsReversed = false,
  isDrawer = false,
  widgetState,
  disableHover = false,
  opening = false,
  closing = false,
  compactIcon = false,
  alertCount = 0,
  zIndexLayers,
  ...props
}: FabButtonProps) {
  const fullText = buttonText || '';
  const displayText = fullText.length > 16 ? `${fullText.slice(0, 16)}…` : fullText;

  const getDisplayIcon = () => {
    if (isDrawer) {
      return icon || 'chat';
    }

    if (widgetState === 'chat') {
      return 'chevron-down';
    }

    return icon || 'chat';
  };

  const displayIcon = getDisplayIcon();
  const isTransitioning = opening || closing;

  return (
    <ButtonWrapper side={side} zIndex={zIndexLayers?.button}>
      <VisualButton
        buttonText={buttonText}
        hasIcon={icon !== 'none'}
        isColorsReversed={isColorsReversed}
        isDrawer={isDrawer}
        widgetState={widgetState}
        disableHover={disableHover}
        opening={opening}
        closing={closing}
        compactIcon={compactIcon}
        overlayZIndex={zIndexLayers?.fabButton.overlay}
        textZIndex={zIndexLayers?.fabButton.text}
        {...props}
      >
        <ButtonIcon
          name={displayIcon}
          className={`icon button-icon ${isTransitioning ? 'transitioning' : ''}`}
        />
        {buttonText && (
          <span className="btn-text" data-truncate="true" title={fullText}>
            {displayText}
          </span>
        )}
        {icon === 'none' && !buttonText && (
          <span className="btn-text" data-truncate="true" title={'Ask me'}>
            Ask me
          </span>
        )}
      </VisualButton>
      {widgetState !== 'chat' && alertCount > 0 && (
        <Alert
          side={side}
          buttonHasText={!!buttonText}
          zIndex={zIndexLayers?.fabButton.alertBadge ?? 20}
          aria-label={`unread count: ${alertCount}`}
        >
          {alertCount}
        </Alert>
      )}
    </ButtonWrapper>
  );
}
